import { FC, useState, useRef, useEffect } from 'react'
import cn from 'classnames'
import { usePrefetchedDraft } from './useDraftForPetition'
import { useGetDraftReviewsQuery, useAddCommentMutation } from 'backoffice/components/Drafts/useDraftsQuery'
import ReviewsModal from 'backoffice/components/Drafts/ReviewsModal'
import { getDateTimeString } from 'lib/helpers/dateHelper'
import { FiCalendar } from 'react-icons/fi'

import { ReactComponent as IconComment } from 'images/icons/annotation.svg'
import { ReactComponent as CaretDownIcon } from 'images/icons/caret-down.svg'

import styles from './DraftReference.module.scss'
import { Draft, DraftReview } from '../Drafts/types/types'

interface DraftReferenceProps {
  dropId: number
}

const DraftReference: FC<DraftReferenceProps> = ({ dropId }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showAllReviews, setShowAllReviews] = useState(false)
  const { data: draft, isLoading } = usePrefetchedDraft(dropId)

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <div className={styles.spinner} />
        <span>Checking for console petition...</span>
      </div>
    )
  }

  if (!draft) {
    return null
  }

  return (
    <div className={styles.draftReference}>
      <div className={styles.draftHeader} onClick={() => setIsExpanded(!isExpanded)}>
        <div className={styles.draftBadge}>
          <span className={styles.consoleBadge}>
            <FiCalendar />
            Console Petition
          </span>
          <span className={styles.draftIdLabel}>#{draft.id}</span>
          <span className={styles.draftTitle}>{draft.data.title}</span>
        </div>
        <CaretDownIcon className={cn(styles.expandIcon, isExpanded && styles.expanded)} />
      </div>

      {isExpanded && <DraftReferenceBody draft={draft} setShowAllReviews={setShowAllReviews} />}

      {showAllReviews && <ReviewsModal draftId={draft.id} onClose={() => setShowAllReviews(false)} />}
    </div>
  )
}

interface DraftReferenceBodyProps {
  draft: Draft
  setShowAllReviews: (show: boolean) => void
}

const DraftReferenceBody: FC<DraftReferenceBodyProps> = ({ draft, setShowAllReviews }) => {
  const { data: draftWithReviews } = useGetDraftReviewsQuery(draft.id)
  const addCommentMutation = useAddCommentMutation()
  const [comment, setComment] = useState('')
  const [showSuccess, setShowSuccess] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const currentDraft = draftWithReviews || draft
  const reviews = currentDraft.recentReviews || []
  const displayedReviews = reviews.slice(0, 3)
  const imageUrl = draft.data.cropImage?.url || draft.data.image?.url

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [comment])

  const handleAddComment = () => {
    const trimmed = comment.trim()
    if (!trimmed) return
    addCommentMutation.mutate(
      { draftId: draft.id, comment: trimmed },
      {
        onSuccess: () => {
          setComment('')
          setShowSuccess(true)
          setTimeout(() => setShowSuccess(false), 2000)
        },
      },
    )
  }

  return (
    <div className={styles.draftBody}>
      <div className={styles.draftInfo}>
        {imageUrl && <img src={imageUrl} alt={draft.data.title} className={styles.draftImage} />}
        <div className={styles.draftDetails}>
          <div className={styles.draftDetailItem}>
            <strong>Created:</strong>
            <span>{getDateTimeString(new Date(draft.createdAt))}</span>
          </div>
          {draft.issuerEmail && (
            <div className={styles.draftDetailItem}>
              <strong>Issuer:</strong>
              <span>{draft.issuerEmail}</span>
            </div>
          )}
          <div className={styles.draftDetailItem}>
            <strong>Supply:</strong>
            <span>{draft.data.mintingConfiguration?.supply ?? 'N/A'}</span>
          </div>
          <p className={styles.draftDescription}>{draft.data.description}</p>
        </div>
      </div>

      <div className={styles.draftActions}>
        {reviews.length > 0 ? (
          <button className={styles.viewCommentsButton} onClick={() => setShowAllReviews(true)}>
            <IconComment />
            View Draft Comments ({reviews.length})
          </button>
        ) : (
          <span className={styles.noComments}>
            <IconComment />
            No Comments
          </span>
        )}
      </div>

      {/* Add comment */}
      <div className={styles.addCommentSection}>
        <label className={styles.addCommentLabel}>Add a comment</label>
        <div className={styles.addCommentInputRow}>
          <textarea
            ref={textareaRef}
            className={styles.commentInput}
            placeholder="Write your comment here..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleAddComment()
              }
            }}
            disabled={addCommentMutation.isLoading}
            rows={1}
          />
          <button
            className={cn(styles.sendButton, showSuccess && styles.sendSuccess)}
            onClick={handleAddComment}
            disabled={!comment.trim() || addCommentMutation.isLoading}
          >
            {addCommentMutation.isLoading ? 'Sending...' : showSuccess ? 'Sent!' : 'Send'}
          </button>
        </div>
      </div>

      {/* Inline preview of recent comments */}
      {displayedReviews.length > 0 && (
        <div className={styles.reviewsInline}>
          <h4 className={styles.reviewsTitle}>
            <IconComment width={16} height={16} />
            Recent Curation Comments
          </h4>
          <div className={styles.reviewsList}>
            {displayedReviews.map((review: DraftReview) => (
              <div key={review.id} className={styles.reviewItem}>
                <div className={styles.reviewHeader}>
                  <span className={styles.reviewerName}>{review.reviewedBy}</span>
                  <span className={styles.reviewDate}>{getDateTimeString(new Date(review.reviewedAt))}</span>
                </div>
                <p className={styles.reviewComment}>{review.comment}</p>
                <span className={cn(styles.reviewStatusBadge, styles[review.status])}>
                  {review.status.replace('_', ' ')}
                </span>
              </div>
            ))}
          </div>
          {reviews.length > 3 && (
            <button
              className={styles.viewCommentsButton}
              onClick={() => setShowAllReviews(true)}
              style={{ marginTop: '0.5rem' }}
            >
              View all {reviews.length} comments
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default DraftReference
